import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CCard } from "@coreui/react-pro";
import type { TrackerResultsDto, TrackerStrategyDto } from "../../../api/hooks/usePortfolioTracker";
import {
  formatDay,
  formatMoney,
  formatPercent,
  formatSignedMoney,
  profitTo,
  takeawayOf,
  toneOf,
} from "../portfolio-tracker";

interface ProfitByStrategyProps {
  results: TrackerResultsDto;
  strategies: Map<string, TrackerStrategyDto>;
  colours: Map<string, string>;
  /** Today, or the day the pointer is on in the chart. */
  day: string;
  asOf: string;
}

/**
 * Each strategy's profit on one day, ranked, with its share of the gains. The order and the bar scale are
 * today's whatever the day, so moving along the chart changes the figures without anything jumping.
 */
export function ProfitByStrategy({ results, strategies, colours, day, asOf }: ProfitByStrategyProps) {
  const { t } = useTranslation("portfolioTracker");

  const order = useMemo(
    () =>
      results.strategies
        .map((_, index) => index)
        .sort((a, b) => results.strategies[b].profit - results.strategies[a].profit),
    [results],
  );
  const scale = useMemo(
    () =>
      Math.max(
        1,
        ...results.strategies.flatMap((strategy) =>
          results.balance.map((point) => Math.abs(profitTo(strategy, point.date))),
        ),
      ),
    [results],
  );

  const profits = results.strategies.map((strategy) => profitTo(strategy, day));
  const net = profits.reduce((sum, profit) => sum + profit, 0);
  const gains = profits.reduce((sum, profit) => sum + Math.max(0, profit), 0);
  const nameOf = (id: string) => strategies.get(id)?.name ?? "";

  const listOf = (names: string[]) =>
    names.length === 1 ? names[0] : t("results.list", { rest: names.slice(0, -1).join(", "), last: names[names.length - 1] });
  const { lead, down } = takeawayOf(
    results.strategies.map((strategy, index) => ({ name: nameOf(strategy.tradingStrategyId), profit: profits[index] })),
  );
  const leadText =
    lead.key === "all"
      ? t("results.takeaway.all", { name: lead.name })
      : lead.key === "spread"
        ? t("results.takeaway.spread", { name: lead.name, percent: formatPercent(lead.percent) })
        : lead.key === "most"
          ? t("results.takeaway.most", { names: listOf(lead.names), percent: formatPercent(lead.percent) })
          : t(`results.takeaway.${lead.key}`);
  const downText = down.length ? ` ${t("results.takeaway.down", { count: down.length, names: listOf(down) })}` : "";

  return (
    <CCard className="tracker-chart-card tracker-side">
      <h3 className="h6 mb-0">{t("results.byStrategy")}</h3>
      <p className={`tracker-side-when${day < asOf ? " is-live" : ""}`}>
        {t("results.onDay", {
          date: formatDay(day),
          worth: formatMoney(results.putIn + net),
          putIn: formatMoney(results.putIn),
        })}
      </p>
      <p className="tracker-takeaway">
        {leadText}
        {downText}
      </p>
      <div className="tracker-bars">
        {order.map((index) => {
          const strategy = results.strategies[index];
          const profit = profits[index];
          const sub =
            profit >= 0.5 && gains > 0
              ? t("results.shareOfGains", { percent: formatPercent((profit / gains) * 100) })
              : profit <= -0.5
                ? t("results.down")
                : t("results.nothingClosed");
          return (
            <div key={strategy.tradingStrategyId} className="tracker-bar-row">
              <span className="tracker-bar-name">
                <i className="tracker-swatch" style={{ background: colours.get(strategy.tradingStrategyId) }} />
                <span className="tracker-bar-text">
                  <span className="tracker-bar-title">{nameOf(strategy.tradingStrategyId)}</span>
                  <small>{sub}</small>
                </span>
              </span>
              <span className="tracker-track">
                <i
                  className={profit >= 0 ? "is-up" : "is-down"}
                  style={{ width: `${((Math.abs(profit) / scale) * 50).toFixed(2)}%` }}
                />
              </span>
              <span className={`tracker-bar-value ${toneOf(profit)}`}>{formatSignedMoney(profit)}</span>
            </div>
          );
        })}
      </div>
      <p className="tracker-chart-note mb-0">{t("results.barsNote")}</p>
    </CCard>
  );
}
